import { genRanHex } from './genRanHex';
import {
  IContentBlock,
  IContentConstructor,
  InlineStyleType,
  SplittedBlocksType,
} from './TextEditor.types';

export class ContentBlock implements IContentBlock {
  text: string = null;

  offset: number = null;

  length: number = null;

  private _styles: Array<InlineStyleType> = null;

  marks: Array<'strong' | 'em'>;

  _key: string = null;

  _type = 'span';

  constructor({
    text = null,
    offset = null,
    length = null,
    inlineStyles = null,
  }: IContentConstructor) {
    this._key = genRanHex(13);
    if (text !== null) this.text = text;
    if (offset !== null) this.offset = offset;
    if (length !== null) this.length = length;
    if (inlineStyles !== null) this.inlineStyles = inlineStyles;
  }

  splitBlock(
    offset: number,
    length: number,
    inlineStyle: 'BOLD' | 'ITALIC',
  ): SplittedBlocksType {
    const noLength = !length || !this.length;
    const sameStyle = this.inlineStyles?.includes(inlineStyle);
    if (noLength || sameStyle) return { blocks: null, remainingStyle: null };

    const startAt = Math.abs(offset - this.offset);
    const endAt = Math.min(startAt + length, this.length);
    let blocks = [];

    if (startAt === 0 && endAt === this.length) {
      if (!this.inlineStyles) this.inlineStyles = [];
      if (!inlineStyle) this.inlineStyles = [...this.inlineStyles, inlineStyle];
      return { blocks: [this], remainingStyle: null };
    }

    if (startAt > 0) {
      const beforeContent = this.text.slice(0, startAt);
      if (beforeContent.length) {
        const beforeContentBlock = new ContentBlock({
          text: beforeContent,
          offset: this.offset,
          length: beforeContent.length,
          inlineStyles: this.inlineStyles,
        });

        blocks.push(beforeContentBlock);
      }
    }
    const inRangeContent = this.text.slice(startAt, endAt);
    if (inRangeContent.length) {
      let newInlineStyles = [];
      if (this.inlineStyles) newInlineStyles = [...this.inlineStyles];
      if (inlineStyle) newInlineStyles = [...newInlineStyles, inlineStyle];
      const inRangeContentBlock = new ContentBlock({
        text: inRangeContent,
        offset: startAt + this.offset,
        length: inRangeContent.length,
        inlineStyles: newInlineStyles?.length ? newInlineStyles : null,
      });

      blocks.push(inRangeContentBlock);
    }

    if (endAt < this.length) {
      const afterContent = this.text.slice(endAt, this.length);
      if (afterContent.length) {
        const afterContentBlock = new ContentBlock({
          text: afterContent,
          offset: this.offset + endAt + this.length,
          length: afterContent.length,
          inlineStyles: this.inlineStyles,
        });
        blocks.push(afterContentBlock);
      }
    }

    const remainingLength = length - inRangeContent.length;
    const remainingStyle = remainingLength
      ? {
          offset: offset + inRangeContent.length,
          length: remainingLength,
          style: inlineStyle,
        }
      : null;

    if (!blocks.length) blocks = null;

    return { blocks, remainingStyle };
  }

  set inlineStyles(styles: Array<InlineStyleType>) {
    if (!styles?.length) {
      this._styles = null;
      this.marks = null;
    }

    if (!this._styles) this._styles = [];
    if (!this.marks) this.marks = [];

    styles.forEach((style) => {
      if (style && !this._styles.includes(style)) this._styles.push(style);
      if (style === 'BOLD' && !this.marks.includes('strong'))
        this.marks.push('strong');
      if (style === 'ITALIC' && !this.marks.includes('em'))
        this.marks.push('em');
    });
  }

  get inlineStyles(): Array<InlineStyleType> {
    return this._styles;
  }
}
